/**
 * ShareProgressManager — Iter 74.
 * Admin/clinician UI for minting and revoking tokenised read-only progress links.
 *
 * Routes:
 *   GET    /api/share/progress/{patient_id}    list active tokens
 *   POST   /api/share/progress                 mint a new token
 *   DELETE /api/share/progress/{token}         revoke a token
 *
 * Public view (no auth required): /share/progress/{token}
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { Sparkles, Copy, Trash2, ChevronLeft, Loader2, Eye, ShieldCheck, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

export default function ShareProgressManager() {
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [daysValid, setDaysValid] = useState(30);
  const [note, setNote] = useState('');

  const reload = async () => {
    const { data } = await api.get(`/share/progress/${patient_id}`);
    setTokens(data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([
          api.get(`/patients/${patient_id}`).catch(() => ({ data: null })),
        ]);
        setPatient(p.data);
        await reload();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_id]);

  const mint = async () => {
    setMinting(true);
    try {
      await api.post('/share/progress', {
        patient_id,
        days_valid: parseInt(daysValid) || 30,
        note: note.trim() || undefined,
      });
      setNote('');
      await reload();
      toast.success('New share link minted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mint failed.');
    } finally { setMinting(false); }
  };

  const revoke = async (token) => {
    if (!await safeConfirm('Revoke this share link? Anyone with the link will lose access immediately.')) return;
    try {
      await api.delete(`/share/progress/${token}`);
      await reload();
      toast.success('Revoked.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed.');
    }
  };

  const buildUrl = (token) => `${window.location.origin}/share/progress/${token}`;
  const copy = (url) => copyToClipboard(url);

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <div className="mb-6">
        <Link to={`/admin/patients/${patient_id}`}
              className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-2"
              data-testid="share-back">
          <ChevronLeft className="w-3 h-3"/> Back to chart
        </Link>
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SHARE PROGRESS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="share-title">
          Shareable progress link
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          {patient?.full_name && <>Patient: <strong>{patient.full_name}</strong> · </>}
          A tokenised read-only URL the patient (or a referring GP) can open to see pain reduction over time.
          No login required. PHI-scrubbed — first name only, no medical-note text.
        </p>
      </div>

      {/* Mint form */}
      <div className="bento-card mb-4">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand"/> Mint a new link
        </h3>
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Days valid</div>
            <input type="number" min="1" max="365" className="input" value={daysValid}
                   onChange={(e) => setDaysValid(e.target.value)} data-testid="share-days"/>
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs text-fg-muted mb-1">Note (optional — e.g. "GP Dr. Smith")</div>
            <input className="input" value={note}
                   onChange={(e) => setNote(e.target.value)} data-testid="share-note"
                   placeholder="GP Dr. Smith / patient mobile"/>
          </label>
        </div>
        <button onClick={mint} disabled={minting}
                className="btn-primary mt-3 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="share-mint">
          {minting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
          Mint link
        </button>
      </div>

      {/* Active tokens */}
      <div className="bento-card">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand"/> Active links ({tokens.filter(t => !t.revoked).length})
        </h3>
        {tokens.length === 0 ? (
          <div className="text-center text-sm text-fg-muted py-6" data-testid="share-empty">
            No links yet. Mint one above to give the patient or their GP read-only access.
          </div>
        ) : (
          <div className="space-y-2" data-testid="share-tokens">
            {tokens.map((t) => {
              const url = buildUrl(t.token);
              const isExpired = t.expires_at && t.expires_at < new Date().toISOString();
              const isRevoked = t.revoked;
              const dead = isExpired || isRevoked;
              return (
                <div key={t.token}
                     className={`flex items-center gap-3 p-3 rounded-md border transition ${dead ? 'border-soft bg-bg-secondary/30 opacity-60' : 'border-soft hover:bg-bg-secondary/30'}`}
                     data-testid={`share-token-${t.token.slice(0, 8)}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-xs truncate text-fg-muted">{url}</span>
                      <Pill kind={dead ? (isRevoked ? 'revoked' : 'expired') : 'active'}/>
                    </div>
                    <div className="text-[11px] text-fg-muted flex items-center gap-3 flex-wrap">
                      <span><Eye className="w-3 h-3 inline mr-0.5"/> {t.view_count || 0} views</span>
                      <span><Calendar className="w-3 h-3 inline mr-0.5"/> Expires {(t.expires_at || '').slice(0, 10)}</span>
                      {t.note && <span className="italic">"{t.note}"</span>}
                    </div>
                  </div>
                  {!dead && (
                    <>
                      <button onClick={() => copy(url)}
                              className="btn-secondary p-1.5"
                              title="Copy link"
                              data-testid={`share-copy-${t.token.slice(0, 8)}`}>
                        <Copy className="w-3.5 h-3.5"/>
                      </button>
                      <button onClick={() => revoke(t.token)}
                              className="btn-secondary p-1.5 text-[#B71C1C]"
                              title="Revoke"
                              data-testid={`share-revoke-${t.token.slice(0, 8)}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ kind }) {
  const m = {
    active:  ['Active',  'bg-[#1B5E20]/10 text-[#1B5E20]'],
    expired: ['Expired', 'bg-[#FFB000]/15 text-[#A86F31]'],
    revoked: ['Revoked', 'bg-[#B71C1C]/10 text-[#B71C1C]'],
  }[kind] || ['—', ''];
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${m[1]}`}>
      {m[0]}
    </span>
  );
}
